import { useCallback, useEffect, useRef, useState } from 'react';

export interface UndoStep {
  /** Puts the world back as it was; both directions go through the same api. */
  undo: () => Promise<void>;
  redo: () => Promise<void>;
}

export interface UndoStack {
  push: (step: UndoStep) => void;
  canUndo: boolean;
  canRedo: boolean;
  undo: () => void;
  redo: () => void;
}

/** Cmd/Ctrl-Z and Shift-Cmd-Z over the steps applied in this session. */
export function useUndoStack(): UndoStack {
  const done = useRef<UndoStep[]>([]);
  const undone = useRef<UndoStep[]>([]);
  const [depth, setDepth] = useState({ done: 0, undone: 0 });

  const sync = useCallback(() => {
    setDepth({ done: done.current.length, undone: undone.current.length });
  }, []);

  const push = useCallback(
    (step: UndoStep) => {
      done.current.push(step);
      undone.current = [];
      sync();
    },
    [sync],
  );

  const undo = useCallback(() => {
    const step = done.current.pop();
    if (step === undefined) return;
    undone.current.push(step);
    sync();
    void step.undo();
  }, [sync]);

  const redo = useCallback(() => {
    const step = undone.current.pop();
    if (step === undefined) return;
    done.current.push(step);
    sync();
    void step.redo();
  }, [sync]);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'z') return;
      // The grid's own editors handle their own undo while one is open.
      const target = event.target;
      if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return;
      event.preventDefault();
      if (event.shiftKey) redo();
      else undo();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undo, redo]);

  return { push, undo, redo, canUndo: depth.done > 0, canRedo: depth.undone > 0 };
}
